import moment from "moment";
import RefreshButton from "../../../../components/refreshButton/refreshButton";
import AssistSessionActionRow from "./assistSessionActionRow";
import AssistSessionQuestionCard from "./assistSessionQuestionCard";
import { groupSessionEntries } from "./assistSessionViewer.helpers";
import {
  AssistActionMetadata,
  AssistQuestionMetadata,
  AssistSessionEntry,
} from "./assistSessionViewer.types";

interface AssistSessionViewerProps {
  entries: AssistSessionEntry[];
  onRefresh?: () => void;
}

/**
 * Renders a RISA-Assist (`extension_session`) audit trail as a list of
 * question-led blocks.
 *
 * Each block carries the question, its full answer, and the screenshots the
 * assistant captured while answering it.
 *
 * @param props - Session entries in chronological order and a refresh handler
 * @returns The session list element
 */
const AssistSessionViewer = (props: AssistSessionViewerProps) => {
  if (props.entries.length === 0) {
    return (
      <div className="assist-session-viewer--empty flex h-full items-center justify-center text-small text-primaryGray-8">
        No activity recorded for this session.
      </div>
    );
  }

  const groups = groupSessionEntries(props.entries);

  return (
    <div className="assist-session-viewer--container flex h-full flex-col">
      <div className="assist-session-viewer--header flex items-center gap-2 border-b border-primaryGray-15 py-2">
        <span className="session-summary text-tiny text-primaryGray-8">
          {moment(props.entries[0]?.timestamp).format("MMM DD, YYYY")}
          <span className="text-primaryGray-13"> · </span>
          {props.entries.length} entries
        </span>
        {props.onRefresh && (
          <div className="ml-auto">
            <RefreshButton onClick={props.onRefresh} />
          </div>
        )}
      </div>
      <ol className="assist-session-viewer--list min-h-0 flex-1 divide-y divide-primaryGray-15 overflow-auto">
        {groups.map((group, index) =>
          group.question ? (
            <li key={`question-${group.question.timestamp}-${index}`}>
              <AssistSessionQuestionCard
                timestamp={group.question.timestamp}
                metadata={
                  (group.question.metadata ?? {}) as AssistQuestionMetadata
                }
                actions={group.actions}
              />
            </li>
          ) : (
            group.actions.map((action, actionIndex) => (
              <li key={`action-${action.timestamp}-${actionIndex}`}>
                <AssistSessionActionRow
                  timestamp={action.timestamp}
                  metadata={(action.metadata ?? {}) as AssistActionMetadata}
                />
              </li>
            ))
          ),
        )}
      </ol>
    </div>
  );
};

export default AssistSessionViewer;
